import { useCallback } from 'react';
import type { RiskScoreWindow } from '@amzn/gbsa-helios-typescript-client';

import { useExportRiskScoresMutation } from '../api';
import {
  usePresignedCsvExport,
  type UsePresignedCsvExportResult,
} from './usePresignedCsvExport';

export interface UseRiskScoreExportParams {
  /** The scope to export — mirror what the table currently shows. */
  readonly businessUnitId: string | undefined;
  readonly timeWindow: RiskScoreWindow | undefined;
  readonly locations: readonly string[];
}

export type UseRiskScoreExportResult = UsePresignedCsvExportResult;

/**
 * Risk-score CSV export (the list page): generates a presigned S3 link scoped to the given business
 * unit, time window, and locations, then downloads it. A thin wrapper over
 * {@link usePresignedCsvExport} — it owns only the input shape; the generate-then-download
 * behaviour is shared with the drill-down export.
 */
export function useRiskScoreExport({
  businessUnitId,
  timeWindow,
  locations,
}: UseRiskScoreExportParams): UseRiskScoreExportResult {
  const mutation = useExportRiskScoresMutation();
  const buildInput = useCallback(() => {
    if (!businessUnitId) return null;
    return { businessUnitId, timeWindow, locations: [...locations] };
  }, [businessUnitId, timeWindow, locations]);
  return usePresignedCsvExport(mutation, buildInput);
}
